import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from '@shared/services/notification.service';
import { IDriftAuditResponse } from '../sync-monitoring.models';
import { SyncMonitoringService } from '../sync-monitoring.service';

/** `tables` are the tables that can be audited (they have an engine table); `selected` preselects some of them, e.g. the one table "Audit this table" was chosen for. */
export interface IDriftAuditDialogData {
	environmentId: string;
	environmentName: string;
	tables: string[];
	selected?: string[];
}

/** Starts a drift audit of all tables or of the selected ones, and closes with the API's answer. */
@Component({
	selector: 'app-drift-audit-dialog',
	templateUrl: './drift-audit-dialog.component.html',
	styleUrls: ['../audit-dialogs.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class DriftAuditDialogComponent {
	public isLoading = false;
	public allTables: boolean;
	public readonly selected: Set<string>;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: IDriftAuditDialogData,
		public dialogRef: MatDialogRef<DriftAuditDialogComponent, IDriftAuditResponse>,
		private syncMonitoringService: SyncMonitoringService,
		private notificationService: NotificationService,
	) {
		this.selected = new Set(data.selected ?? []);
		this.allTables = this.selected.size === 0;
	}

	get canStart(): boolean {
		return !this.isLoading && (this.allTables || this.selected.size > 0);
	}

	toggleTable(table: string, checked: boolean): void {
		if (checked) {
			this.selected.add(table);
		} else {
			this.selected.delete(table);
		}
		this.allTables = false;
	}

	onStart(): void {
		if (!this.canStart) {
			return;
		}
		this.isLoading = true;
		this.dialogRef.disableClose = true;
		this.syncMonitoringService.startDriftAudit(this.data.environmentId, this.allTables ? undefined : [...this.selected]).subscribe({
			next: (response) => this.dialogRef.close(response),
			error: () => {
				this.isLoading = false;
				this.dialogRef.disableClose = false;
				this.notificationService.notify(['administration.sync-monitoring.errors.audit-failed'], [], 'error');
			},
		});
	}

	onCancel(): void {
		this.dialogRef.close();
	}
}
